import {
  Plus,
  Sparkles,
  Check,
  AlertTriangle,
  ArrowUpDown,
  AlertCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { FindingsFilter } from "./comparison-types";

export interface FindingsFilterRibbonProps {
  findingsFilter: FindingsFilter;
  setFindingsFilter: (filter: FindingsFilter) => void;
  totalDiffs: number;
  newProblemsCount: number;
  newStrengthsCount: number;
  resolvedCount: number;
  lostStrengthsCount: number;
  changedCount: number;
  persistentCount: number;
  canEdit: boolean;
  isTrackerConfigured?: boolean;
  onBulkCreateIssues?: () => void;
}

export function FindingsFilterRibbon({
  findingsFilter,
  setFindingsFilter,
  totalDiffs,
  newProblemsCount,
  newStrengthsCount,
  resolvedCount,
  lostStrengthsCount,
  changedCount,
  persistentCount,
  canEdit,
  isTrackerConfigured,
  onBulkCreateIssues,
}: FindingsFilterRibbonProps) {
  return (
    <div className="flex items-center justify-between gap-2 border-b border-border/70 pb-2 flex-wrap">
      <div className="flex items-center gap-1.5 flex-wrap">
        <button
          type="button"
          onClick={() => setFindingsFilter("all")}
          className={cn(
            "flex items-center gap-1.5 px-2.5 py-1 rounded-sm text-xs font-medium transition-colors cursor-pointer border",
            findingsFilter === "all"
              ? "bg-muted text-foreground font-semibold border-border"
              : "border-transparent text-muted-foreground hover:bg-muted/60 hover:text-foreground",
          )}
        >
          <span>All differences</span>
          <span className="font-mono text-xs tabular-nums">{totalDiffs}</span>
        </button>

        <button
          type="button"
          onClick={() => setFindingsFilter("new")}
          className={cn(
            "flex items-center gap-1.5 px-2.5 py-1 rounded-sm text-xs font-medium transition-colors cursor-pointer",
            findingsFilter === "new"
              ? "bg-destructive/20 text-destructive font-semibold border border-destructive/40"
              : newProblemsCount > 0
                ? "text-destructive hover:bg-destructive/10"
                : "text-muted-foreground hover:bg-muted/60",
          )}
        >
          <Plus className="size-3" />
          <span>New problems</span>
          <span className="font-mono text-xs tabular-nums">
            {newProblemsCount}
          </span>
        </button>

        {newStrengthsCount > 0 && (
          <button
            type="button"
            onClick={() => setFindingsFilter("strengths")}
            className={cn(
              "flex items-center gap-1.5 px-2.5 py-1 rounded-sm text-xs font-medium transition-colors cursor-pointer",
              findingsFilter === "strengths"
                ? "bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 font-semibold border border-emerald-500/40"
                : "text-emerald-700 dark:text-emerald-400 hover:bg-emerald-500/10",
            )}
          >
            <Sparkles className="size-3" />
            <span>New strengths</span>
            <span className="font-mono text-xs tabular-nums">
              {newStrengthsCount}
            </span>
          </button>
        )}

        <button
          type="button"
          onClick={() => setFindingsFilter("resolved")}
          className={cn(
            "flex items-center gap-1.5 px-2.5 py-1 rounded-sm text-xs font-medium transition-colors cursor-pointer",
            findingsFilter === "resolved"
              ? "bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 font-semibold border border-emerald-500/40"
              : resolvedCount > 0
                ? "text-emerald-700 dark:text-emerald-400 hover:bg-emerald-500/10"
                : "text-muted-foreground hover:bg-muted/60",
          )}
        >
          <Check className="size-3" />
          <span>Resolved</span>
          <span className="font-mono text-xs tabular-nums">
            {resolvedCount}
          </span>
        </button>

        {lostStrengthsCount > 0 && (
          <button
            type="button"
            onClick={() => setFindingsFilter("lost_strengths")}
            className={cn(
              "flex items-center gap-1.5 px-2.5 py-1 rounded-sm text-xs font-medium transition-colors cursor-pointer",
              findingsFilter === "lost_strengths"
                ? "bg-amber-500/20 text-amber-700 dark:text-amber-300 font-semibold border border-amber-500/40"
                : "text-amber-700 dark:text-amber-400 hover:bg-amber-500/10",
            )}
          >
            <AlertTriangle className="size-3" />
            <span>Strengths lost</span>
            <span className="font-mono text-xs tabular-nums">
              {lostStrengthsCount}
            </span>
          </button>
        )}

        {changedCount > 0 && (
          <button
            type="button"
            onClick={() => setFindingsFilter("changed")}
            className={cn(
              "flex items-center gap-1.5 px-2.5 py-1 rounded-sm text-xs font-medium transition-colors cursor-pointer",
              findingsFilter === "changed"
                ? "bg-amber-500/20 text-amber-700 dark:text-amber-300 font-semibold border border-amber-500/40"
                : "text-amber-700 dark:text-amber-400 hover:bg-amber-500/10",
            )}
          >
            <ArrowUpDown className="size-3" />
            <span>Severity shifted</span>
            <span className="font-mono text-xs tabular-nums">
              {changedCount}
            </span>
          </button>
        )}

        <button
          type="button"
          onClick={() => setFindingsFilter("persisting")}
          className={cn(
            "flex items-center gap-1.5 px-2.5 py-1 rounded-sm text-xs font-medium transition-colors cursor-pointer border",
            findingsFilter === "persisting"
              ? "bg-muted text-foreground font-semibold border-border"
              : "border-transparent text-muted-foreground hover:bg-muted/60 hover:text-foreground",
          )}
        >
          <AlertCircle className="size-3" />
          <span>Persisting</span>
          <span className="font-mono text-xs tabular-nums">
            {persistentCount}
          </span>
        </button>
      </div>

      {canEdit &&
        isTrackerConfigured &&
        onBulkCreateIssues &&
        newProblemsCount > 0 && (
          <Button
            variant="outline"
            size="sm"
            className="shrink-0 h-7 text-xs text-destructive hover:bg-destructive/10 border-destructive/30"
            onClick={onBulkCreateIssues}
          >
            Create issues ({newProblemsCount})
          </Button>
        )}
    </div>
  );
}
